import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useMatter } from "../context/MatterContext";
import { postClientContactUs, patchBhpClientContact } from "../helpers/apiHelper";
import { ContactValidators } from "../helpers/validationHelper";
import "../styles/ContactUsPage.css";

const CONTACT_SUBJECT_OPTIONS = [
  { value: "", label: "Selecione um assunto" },
  { value: "ATUALIZAR_CORRIGIR_DADOS", label: "Atualizar/Corrigir dados" },
  { value: "ENVIAR_DOCUMENTOS", label: "Enviar documentos" },
  {
    value: "QUESTOES_REFERENTES_ADVOGADO_PARCEIRO",
    label: "Questões referentes ao advogado parceiro",
  },
  { value: "QUESTIONARIO_DANOS", label: "Questionário de danos" },
  { value: "PORTAL_DO_CLIENTE", label: "Portal do Cliente" },
  { value: "PORTAL_DO_ADVOGADO", label: "Portal do Advogado" },
  { value: "CLIENTE_FALECIDO", label: "Cliente falecido" },
  { value: "DUVIDAS_SOBRE_CASO", label: "Dúvidas sobre o caso" },
  {
    value: "CONFIRMACAO_PARTICIPACAO_ACAO",
    label: "Confirmação de participação na ação",
  },
  { value: "CLIENTE_MENOR_IDADE", label: "Cliente menor de idade" },
  { value: "OUTROS", label: "Outros" },
];

const MESSAGE_MAX_LENGTH = 2000;
const MESSAGE_MIN_LENGTH = 10;
const SUCCESS_REDIRECT_DELAY_MS = 4000;

export default function ContactUsPage() {
  const navigate = useNavigate();
  const { tokens, applyRefreshedTokens } = useAuth();
  const { party, matter, setParty } = useMatter();

  const registeredEmail = party?.email || "";

  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [useDifferentEmail, setUseDifferentEmail] = useState(!registeredEmail);
  const [contactEmail, setContactEmail] = useState(registeredEmail);
  const [touched, setTouched] = useState({
    subject: false,
    message: false,
    contactEmail: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [success, setSuccess] = useState(false);
  const [confirmedEmail, setConfirmedEmail] = useState("");

  const partyId = party?.id || party?.partyId || party?.Id || "";
  const matterId = matter?.id || matter?.matterId || matter?.Id || "";
  const cpf = party?.cpf || "";

  const messageLength = message.length;
  const trimmedMessage = String(message || "").trim();
  const authToken =
    tokens?.idToken ||
    tokens?.accessToken ||
    localStorage.getItem("idToken") ||
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("idToken") ||
    sessionStorage.getItem("accessToken") ||
    "";

  const effectiveEmail = useDifferentEmail
    ? ContactValidators.normalizeEmail(contactEmail)
    : ContactValidators.normalizeEmail(registeredEmail);

  const emailChanged =
    useDifferentEmail &&
    ContactValidators.isValidEmail(effectiveEmail) &&
    effectiveEmail !== ContactValidators.normalizeEmail(registeredEmail);

  useEffect(() => {
    if (!success) return undefined;

    const timeoutId = window.setTimeout(() => {
      navigate("/dashboard", { replace: true });
    }, SUCCESS_REDIRECT_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
  }, [navigate, success]);

  const subjectError = useMemo(() => {
    if (!touched.subject) return "";
    return subject ? "" : "Selecione um assunto.";
  }, [subject, touched.subject]);

  const messageError = useMemo(() => {
    if (!touched.message) return "";
    if (!trimmedMessage) return "Escreva sua mensagem.";
    if (trimmedMessage.length < MESSAGE_MIN_LENGTH) {
      return `Use pelo menos ${MESSAGE_MIN_LENGTH} caracteres.`;
    }
    if (trimmedMessage.length > MESSAGE_MAX_LENGTH) {
      return `Use no máximo ${MESSAGE_MAX_LENGTH} caracteres.`;
    }
    return "";
  }, [touched.message, trimmedMessage]);

  const contactEmailError = useMemo(() => {
    if (!useDifferentEmail || !touched.contactEmail) return "";
    if (!contactEmail.trim()) return "Informe o e-mail que devemos usar.";
    if (!ContactValidators.isValidEmail(contactEmail)) return "E-mail inválido.";
    return "";
  }, [useDifferentEmail, touched.contactEmail, contactEmail]);

  const canSubmit = useMemo(() => {
    if (submitting) return false;
    if (!partyId || !matterId) return false;
    if (!ContactValidators.isValidEmail(effectiveEmail)) return false;
    return (
      Boolean(subject) &&
      trimmedMessage.length >= MESSAGE_MIN_LENGTH &&
      trimmedMessage.length <= MESSAGE_MAX_LENGTH
    );
  }, [submitting, partyId, matterId, subject, trimmedMessage.length, effectiveEmail]);

  const markTouched = (field) => {
    setTouched((current) => ({ ...current, [field]: true }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError("");
    setSuccess(false);
    setTouched({ subject: true, message: true, contactEmail: true });

    if (!canSubmit || !authToken) {
      if (!partyId || !matterId) {
        setSubmitError("Selecione um caso antes de falar com a equipe.");
      } else if (!ContactValidators.isValidEmail(effectiveEmail)) {
        setSubmitError("Informe um e-mail válido para receber nossa resposta.");
      } else {
        setSubmitError("Sessão expirada. Faça login novamente.");
      }
      return;
    }

    setSubmitting(true);
    try {
      if (emailChanged) {
        if (!cpf) {
          throw new Error(
            "Não foi possível identificar seu CPF para atualizar o e-mail. Faça login novamente.",
          );
        }
        const patchResult = await patchBhpClientContact(authToken, partyId, {
          cpf,
          email: effectiveEmail,
        });
        // Changing the email invalidates the current Cognito refresh token; the backend
        // reissues a fresh pair (and re-sets the refresh-token cookie) in the same response
        // so this session survives instead of being force-logged-out on the next refresh.
        applyRefreshedTokens(patchResult);
        setParty((prev) => ({ ...(prev || {}), email: effectiveEmail }));
      }

      await postClientContactUs(authToken, {
        partyId,
        matterId,
        subject,
        message: trimmedMessage,
        contactEmail: effectiveEmail,
      });
      setConfirmedEmail(effectiveEmail);
      setSuccess(true);
      // Deliberately not resetting subject/message/useDifferentEmail/touched here:
      // this replaces the whole form with the success view below (see render), rather
      // than patching individual fields (e.g. swapping the email <input> for a <div>)
      // in place. Session-replay tooling (AWS RUM) can reparent/wrap form inputs for
      // masking, and an in-place swap of that exact node crashes React's reconciler
      // with "NotFoundError: Failed to execute 'removeChild'" - a full subtree replace
      // avoids it.
    } catch (error) {
      setSubmitError(
        error?.message || "Não foi possível enviar sua mensagem. Tente novamente.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="contactUsPage">
        <div className="contactUsShell">
          <div className="contactUsCard contactUsSuccessCard">
            <div className="contactUsEyebrow">Atendimento ao cliente</div>
            <h1 className="contactUsTitle">Mensagem enviada!</h1>
            <div className="contactUsSuccess">
              Sua mensagem foi enviada com sucesso. Enviamos uma confirmação para{" "}
              {confirmedEmail}. Você será redirecionado para o painel.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="contactUsPage">
      <div className="contactUsShell">
        <form className="contactUsCard" onSubmit={handleSubmit}>
          <button
            type="button"
            className="contactUsClose"
            aria-label="Fechar"
            onClick={() => navigate(-1)}
            disabled={submitting}
          >
            ×
          </button>

          <div className="contactUsEyebrow">Atendimento ao cliente</div>
          <h1 className="contactUsTitle">Fale conosco</h1>
          <p className="contactUsSubtitle">
            Envie uma mensagem para nossa equipe sobre o caso selecionado.
          </p>

          <div className="contactUsMatterSummary">
            <div>
              <span className="contactUsMatterLabel">Caso</span>
              <strong>{matter?.name || "Nenhum caso selecionado"}</strong>
            </div>
            <div>
              <span className="contactUsMatterLabel">Cliente</span>
              <strong>{party?.name || "Nenhum cliente selecionado"}</strong>
            </div>
          </div>

          <label className="contactUsLabel" htmlFor="contactUsSubject">
            Assunto
          </label>
          <select
            id="contactUsSubject"
            className="contactUsSelect"
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            onBlur={() => markTouched("subject")}
          >
            {CONTACT_SUBJECT_OPTIONS.map((option) => (
              <option key={option.value || "empty"} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {subjectError ? <div className="contactUsError">{subjectError}</div> : null}

          <label className="contactUsLabel" htmlFor="contactUsEmail">
            Seu e-mail
          </label>
          <input
            id="contactUsEmail"
            className={
              useDifferentEmail ? "contactUsInput" : "contactUsInput contactUsInputReadOnly"
            }
            type="email"
            value={useDifferentEmail ? contactEmail : registeredEmail}
            readOnly={!useDifferentEmail}
            onChange={(event) => setContactEmail(event.target.value)}
            onBlur={() => markTouched("contactEmail")}
            placeholder="seuemail@exemplo.com"
            autoComplete="email"
          />
          {registeredEmail ? (
            <button
              type="button"
              className="contactUsLinkBtn"
              onClick={() => {
                if (useDifferentEmail) {
                  setUseDifferentEmail(false);
                  setContactEmail(registeredEmail);
                } else {
                  setUseDifferentEmail(true);
                  setContactEmail("");
                }
              }}
            >
              {useDifferentEmail
                ? `Usar o e-mail cadastrado (${registeredEmail})`
                : "Usar um e-mail diferente"}
            </button>
          ) : null}
          {contactEmailError ? (
            <div className="contactUsError">{contactEmailError}</div>
          ) : null}

          <label className="contactUsLabel" htmlFor="contactUsMessage">
            Mensagem
          </label>
          <textarea
            id="contactUsMessage"
            className="contactUsTextarea"
            value={message}
            onChange={(event) => setMessage(event.target.value.slice(0, MESSAGE_MAX_LENGTH))}
            onBlur={() => markTouched("message")}
            rows={7}
            placeholder="Escreva sua mensagem aqui."
          />
          <div className="contactUsMeta">
            <span>{messageLength}/{MESSAGE_MAX_LENGTH}</span>
          </div>
          {messageError ? <div className="contactUsError">{messageError}</div> : null}

          {submitError ? <div className="contactUsError">{submitError}</div> : null}

          <button
            type="submit"
            className="contactUsSubmit"
            disabled={!canSubmit}
          >
            {submitting ? "Enviando..." : "Enviar mensagem"}
          </button>
        </form>
      </div>
    </div>
  );
}
